import SearchInput from "@dashboard/components/AppLayout/ListFilters/components/SearchInput";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination/TablePaginationWithContext";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import { AtticLocationKind, type AtticLocationRowFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { MONO_FONT_FAMILY } from "@dashboard/styles/monoFontFamily";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Button, Checkbox, Select, Skeleton, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { kindMessages, locationMessages, sectionMessages } from "../../messages";
import {
  atticLocationAddPath,
  type AtticLocationListUrlFilters,
  atticLocationUrl,
} from "../../urls";
import { ruleSummary } from "../../utils";
import { AtticInventoryNav } from "../AtticInventoryNav/AtticInventoryNav";

const numberOfColumns = 6;

/** The kind select's first option: no kind filter. */
export const KIND_ALL = "";

/** `parent=""` in the URL means top-level only; absent means everything. */
export const ROOTS_ONLY = "";

export interface AtticLocationListPageProps {
  /** undefined while loading. */
  locations: AtticLocationRowFragment[] | undefined;
  loadFailed: boolean;
  filters: AtticLocationListUrlFilters;
  onFiltersChange: (changes: Partial<AtticLocationListUrlFilters>) => void;
}

export const AtticLocationListPage = ({
  locations,
  loadFailed,
  filters,
  onFiltersChange,
}: AtticLocationListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const kindOptions = [
    { label: intl.formatMessage(locationMessages.kindAll), value: KIND_ALL },
    ...Object.values(AtticLocationKind).map(kind => ({
      label: intl.formatMessage(kindMessages[kind]),
      value: kind,
    })),
  ];
  const kindValue = kindOptions.find(option => option.value === (filters.kind ?? KIND_ALL));
  const rootsOnly = filters.parent === ROOTS_ONLY;
  const filtered = !!(filters.query || filters.kind || filters.parent !== undefined);

  return (
    <ListPageLayout>
      <TopNav
        title={intl.formatMessage(sectionMessages.locations)}
        subtitle={
          <Text size={2} color="default2">
            {intl.formatMessage(locationMessages.subtitle)}
          </Text>
        }
      >
        <Button
          variant="primary"
          onClick={() => navigate(atticLocationAddPath)}
          data-test-id="attic-location-create"
        >
          {intl.formatMessage(locationMessages.create)}
        </Button>
      </TopNav>
      <AtticInventoryNav current="locations" />
      <DashboardCard data-test-id="attic-location-list">
        <DashboardCard.Content paddingX={0}>
          <Box
            display="flex"
            alignItems="flex-end"
            gap={4}
            flexWrap="wrap"
            paddingX={6}
            paddingTop={4}
            paddingBottom={2}
          >
            <Box flexGrow="1" __minWidth="240px">
              <SearchInput
                initialSearch={filters.query ?? ""}
                placeholder={intl.formatMessage(locationMessages.searchPlaceholder)}
                onSearchChange={query => onFiltersChange({ query: query || undefined })}
              />
            </Box>
            <Select
              size="small"
              __width={180}
              label={intl.formatMessage(locationMessages.kind)}
              options={kindOptions}
              value={kindValue ?? kindOptions[0]}
              onChange={option =>
                onFiltersChange({ kind: (option?.value || undefined) as string | undefined })
              }
              data-test-id="attic-location-kind"
            />
            <Checkbox
              checked={rootsOnly}
              onCheckedChange={checked =>
                onFiltersChange({ parent: checked === true ? ROOTS_ONLY : undefined })
              }
              data-test-id="attic-location-roots"
            >
              <Text size={3}>{intl.formatMessage(locationMessages.rootsOnly)}</Text>
            </Checkbox>
          </Box>
          {filters.parent && (
            <Box paddingX={6} paddingBottom={2}>
              <Text size={2} color="default2" data-test-id="attic-location-parent-chip">
                {intl.formatMessage(locationMessages.parent)}{" "}
                <Text size={2} style={{ fontFamily: MONO_FONT_FAMILY }}>
                  {filters.parent}
                </Text>
              </Text>
            </Box>
          )}
          <ResponsiveTable footer={<TablePaginationWithContext component="div" />}>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(locationMessages.code)}</TableCell>
                <TableCell>{intl.formatMessage(locationMessages.name)}</TableCell>
                <TableCell>{intl.formatMessage(locationMessages.kind)}</TableCell>
                <TableCell>{intl.formatMessage(locationMessages.parent)}</TableCell>
                <TableCell>{intl.formatMessage(locationMessages.rules)}</TableCell>
                <TableCell align="right">{intl.formatMessage(locationMessages.units)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {loadFailed ? (
                <TableRowLink data-test-id="attic-location-list-failed">
                  <TableCell colSpan={numberOfColumns}>
                    <Text size={3} color="critical1">
                      {intl.formatMessage(sectionMessages.loadFailed)}
                    </Text>
                  </TableCell>
                </TableRowLink>
              ) : (
                renderCollection(
                  locations,
                  row => (
                    <TableRowLink
                      key={row ? row.id : "skeleton"}
                      hover={!!row}
                      onClick={row ? () => navigate(atticLocationUrl(row.code)) : undefined}
                      data-test-id="attic-location-row"
                    >
                      <TableCell>
                        {row ? (
                          <Box display="flex" gap={2} alignItems="center">
                            <Link
                              to={atticLocationUrl(row.code)}
                              onClick={event => event.stopPropagation()}
                            >
                              <Text
                                size={3}
                                fontWeight="medium"
                                style={{ fontFamily: MONO_FONT_FAMILY }}
                              >
                                {row.code}
                              </Text>
                            </Link>
                            {!row.isActive && (
                              <Text
                                size={1}
                                color="warning1"
                                data-test-id="attic-location-inactive"
                              >
                                {intl.formatMessage(locationMessages.inactive)}
                              </Text>
                            )}
                          </Box>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell>{row ? <Text size={3}>{row.name}</Text> : <Skeleton />}</TableCell>
                      <TableCell>
                        {row ? (
                          <Text size={3}>{intl.formatMessage(kindMessages[row.kind])}</Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell>
                        {row ? (
                          <Text size={3} color="default2" style={{ fontFamily: MONO_FONT_FAMILY }}>
                            {row.parent?.code ?? ""}
                          </Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell>
                        {row ? (
                          <Text size={3} color="default2" data-test-id="attic-location-rules">
                            {ruleSummary(row) || intl.formatMessage(locationMessages.ruleAny)}
                          </Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell align="right" data-test-id="attic-location-units">
                        {row ? <Text size={3}>{row.unitCount}</Text> : <Skeleton />}
                      </TableCell>
                    </TableRowLink>
                  ),
                  () => (
                    <TableRowLink data-test-id="attic-location-list-empty">
                      <TableCell colSpan={numberOfColumns}>
                        <Text size={3} color="default2">
                          {intl.formatMessage(
                            filtered ? locationMessages.noMatches : locationMessages.empty,
                          )}
                        </Text>
                      </TableCell>
                    </TableRowLink>
                  ),
                )
              )}
            </TableBody>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default AtticLocationListPage;
